export type Device = {
  client_id: string
  kind: "camera" | "buzzer"
  display_name: string
  room: string
  buzzer_ids: string[]
  alerts_enabled: boolean
  online: boolean
  last_seen_ms: number
  battery_percent?: number | null
}

export type Frame = {
  frame_id: number
  timestamp_ms: number
  received_ms: number
  image: { width: number; height: number; data: string; mirrored: boolean; annotated: boolean }
  prediction: {
    class_name: string | null
    confidence: number | null
    face_present: boolean
    landmarks: { x: number; y: number; z: number }[]
    head_pose: { yaw: number; pitch: number; roll: number } | null
    model_version: string | null
  } | null
}

export type TrainingSession = {
  session_id: string
  client_id: string
  status: "ready" | "collecting" | "awaiting_next" | "training" | "completed" | "cancelled" | "failed"
  step_index: number
  target_samples: number
  steps: {
    id: string
    label: string
    instruction: string
    collected_samples: number
    urgency: string | null
  }[]
  model_version: string | null
  revision: number
  created_ms: number
  updated_ms: number
  metrics: Record<string, number>
  error: string | null
}

// HTTP replies and WebSocket updates can arrive in either order.
export function latestTraining(previous: TrainingSession | null, incoming: TrainingSession | null) {
  if (!incoming) return previous
  if (!previous) return incoming
  if (previous.session_id === incoming.session_id) {
    return previous.revision > incoming.revision ? previous : incoming
  }
  return previous.created_ms > incoming.created_ms ? previous : incoming
}

export type Alert = {
  alert_id: string
  class_name: string
  label: string
  room: string
  urgency: string
  status: "open" | "acknowledged" | "expired"
  created_ms: number
  expires_ms: number
  deliveries: Record<
    string,
    { broker_accepted_ms: number | null; received_ms: number | null; attempts: number }
  >
}

export type Snapshot = {
  client: Device
  frame: Frame | null
  training: TrainingSession | null
  alerts: Alert[]
  mqtt_connected: boolean
}

export const API_BASE = (process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000").replace(/\/$/, "")

export async function api<T>(token: string, path: string, method = "GET", body?: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}/api/v1${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    cache: "no-store",
    signal: AbortSignal.timeout(8000),
  })
  const result = await response.json()
  if (!response.ok) {
    const message = typeof result.detail === "string" ? result.detail : JSON.stringify(result.detail)
    throw new Error(message ?? `Request failed (${response.status})`)
  }
  return result as T
}

export function eventSocket(clientId: string) {
  const url = new URL(`${API_BASE}/api/v1/clients/${encodeURIComponent(clientId)}/events`)
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:"
  return new WebSocket(url)
}
